/**
 * Per-page access rules (user decision 29/09/2026): SPN004 ticks who may open ONE sidebar page,
 * per person or per department, from the Sidebar tab's popup. A revoke applies to admins too; a
 * grant opens the page for someone the HR rules leave out. Precedence: person rule > department
 * rule > everything else (key overrides, HR rules, admin). SPN004 (NAV_TOGGLE_ADMIN_ECODES) is
 * never affected.
 */
import { NAV_TOGGLE_ADMIN_ECODES, NAV_TOGGLE_LOCKED_PATHS } from './nav-toggles.contract.js';
/**
 * Every sidebar page a rule may target, with the extra path prefixes it owns (a leaf's
 * `activeUnder`). Label-free mirror of the web NAV tree + footer links (a web spec pins the two
 * equal), kept here so the API can resolve a path to its page without the UI tree.
 */
export const NAV_PAGES = [
    { to: '/game' },
    { to: '/marketing' },
    { to: '/marketing/app-spy' },
    { to: '/marketing/playable-ads' },
    { to: '/marketing/localize' },
    { to: '/marketing/competitor' },
    { to: '/marketing/creative-spy' },
    { to: '/marketing/campaign-editor' },
    { to: '/marketing/ua-alerts' },
    { to: '/monetization' },
    { to: '/monetization/ecpm' },
    { to: '/monetization/admob-mediation' },
    { to: '/monetization/encrypt' },
    { to: '/monetization/app-assignment' },
    { to: '/monetization/mo-lead' },
    { to: '/developer' },
    { to: '/developer/encrypt' },
    { to: '/developer/ads-decode' },
    { to: '/issues', activeUnder: ['/issues'] },
    { to: '/docs', activeUnder: ['/docs'] },
    { to: '/growth/ua-bot-console' },
    { to: '/growth/aso-translate' },
    { to: '/growth/firebase-rc' },
    { to: '/growth/event-analytics' },
    { to: '/growth/site-content' },
    { to: '/employees' },
    { to: '/hr/leave' },
    { to: '/hr/config' },
    { to: '/hr/login-logs' },
    { to: '/products' },
    { to: '/admin/store-machines' },
    { to: '/okr/admin' },
    { to: '/finance/commission' },
    { to: '/finance/dividend-distribution' },
    { to: '/quiz' },
    { to: '/quiz/host' },
    { to: '/exam/admin/tests' },
    { to: '/exam/admin/questions' },
    { to: '/exam/admin/create' },
    { to: '/profile' },
    { to: '/leave' },
    { to: '/exam/my' },
    { to: '/handbook' },
    { to: '/travel-expense' },
    { to: '/help' },
];
/** Whether `path` is a page a rule may target (a NAV page, never a locked one). */
export function isRulePagePath(path) {
    return !NAV_TOGGLE_LOCKED_PATHS.includes(path) && NAV_PAGES.some((p) => p.to === path);
}
/**
 * The page that owns `pathname`: an exact match on a page's `to` or one of its `activeUnder`
 * prefixes wins; otherwise the longest `to` that is a '/'-bounded prefix. Undefined when no page
 * owns it (e.g. '/', '/admin', an unknown route).
 */
export function pageForPath(pathname) {
    const candidates = [];
    for (const p of NAV_PAGES) {
        candidates.push({ match: p.to, to: p.to });
        for (const prefix of p.activeUnder ?? [])
            candidates.push({ match: prefix, to: p.to });
    }
    const exact = candidates.find((c) => c.match === pathname);
    if (exact)
        return exact.to;
    let best;
    for (const c of candidates) {
        if (pathname.startsWith(`${c.match}/`) && (!best || c.match.length > best.match.length))
            best = c;
    }
    return best?.to;
}
/**
 * Pure: resolve one person's page rules. For each path, the person rule wins over a department
 * rule for `dCode`; SPN004 (and anyone without an eCode) gets nothing. Rows with an unknown
 * targetType/effect or a non-rule path are ignored, so a stale row degrades to "no rule".
 */
export function resolvePageRules(rows, eCode, dCode) {
    if (!eCode || NAV_TOGGLE_ADMIN_ECODES.includes(eCode))
        return { pageGrants: [], pageBlocks: [] };
    const byPath = new Map();
    for (const r of rows) {
        if (r.effect !== 'grant' && r.effect !== 'revoke')
            continue;
        if (!isRulePagePath(r.path))
            continue;
        const slot = byPath.get(r.path) ?? {};
        if (r.targetType === 'person' && r.target === eCode)
            slot.person = r.effect;
        else if (r.targetType === 'dept' && dCode && r.target === dCode)
            slot.dept = r.effect;
        else
            continue;
        byPath.set(r.path, slot);
    }
    const pageGrants = [];
    const pageBlocks = [];
    for (const [path, slot] of byPath) {
        const effect = slot.person ?? slot.dept;
        if (effect === 'grant')
            pageGrants.push(path);
        else if (effect === 'revoke')
            pageBlocks.push(path);
    }
    return { pageGrants: pageGrants.sort(), pageBlocks: pageBlocks.sort() };
}
/** Cap on changes per Save — generous for a whole-company tick, bounded against a hostile body. */
export const PAGE_RULE_MAX_CHANGES = 500;
//# sourceMappingURL=access-page-rules.contract.js.map